import { createSlice } from '@reduxjs/toolkit';

import {
  QCTaskDefinitionState,
  QCTaskDefinitionUpdate,
  QCTaskDefinitionFetchAction,
  QCTaskDefinitionUpdateSuccess,
  QCTaskDefinitionStatusFetchAction,
  QCTaskDefinitionFetchSuccessAction,
  QCTaskDefinitionStatusFetchSuccessAction,
} from './QCTaskDefinition.type';
import { createSliceHook, makeActionCreator } from '../helpers';

const { act, actReject, actReq } = makeActionCreator<QCTaskDefinitionState>();

const initialState: QCTaskDefinitionState = {
  data: [],
  loading: false,
  statusData: {},
  currentContext: {
    clinicalTrialId: null,
    patientId: null,
    visitId: null,
  },
};

export const qcTaskDefinitionSlice = createSlice({
  name: 'qcTaskDefinition',
  initialState,
  reducers: {
    clear: state => {
      state.data = initialState.data;
      state.loading = initialState.loading;
      state.statusData = initialState.statusData;
      state.currentContext = initialState.currentContext;
    },
    fetchQCTaskDefinitions: act<QCTaskDefinitionFetchAction['payload']>(
      (payload, state) => {
        return {
          ...state,
          data: [],
          loading: true,
          currentContext: {
            clinicalTrialId: payload.clinicalTrialId,
            patientId: payload.patientId,
            visitId: payload.visitId,
          },
        };
      }
    ),
    fetchQCTaskDefinitionsSuccess: act<
      QCTaskDefinitionFetchSuccessAction['payload']
    >((data, state) => {
      return { ...state, data: data || [], loading: false };
    }),
    fetchQCTaskDefinitionsFailure: actReject<Error>(),

    updateQCTaskDefinition: actReq<QCTaskDefinitionUpdate['payload']>(),
    updateQCTaskDefinitionSuccess: act<
      QCTaskDefinitionUpdateSuccess['payload']
    >((data, state) => {
      if (!data) return state;

      const updateStateData = state.data.map(definitionTask =>
        definitionTask.id !== data.id ? definitionTask : data
      );
      return { ...state, data: updateStateData, loading: false };
    }),
    updateQCTaskDefinitionFailure: actReject<Error>(),

    fetchQCTaskDefinitionStatus:
      actReq<QCTaskDefinitionStatusFetchAction['payload']>(),
    fetchQCTaskDefinitionStatusSuccess: act<
      QCTaskDefinitionStatusFetchSuccessAction['payload']
    >((data, state) => {
      if (!data) return state;

      const updateStateStatus = state.data?.map(definitionTask =>
        definitionTask.id !== data.id
          ? definitionTask
          : {
              ...definitionTask,
              executionStatus: data.executionStatus,
            }
      );
      return { ...state, data: updateStateStatus, loading: false };
    }),
    fetchQCTaskDefinitionStatusFailure: actReject<Error>(),
  },
});

export const {
  fetchQCTaskDefinitions,
  fetchQCTaskDefinitionsSuccess,
  fetchQCTaskDefinitionsFailure,
  updateQCTaskDefinition,
  updateQCTaskDefinitionSuccess,
  updateQCTaskDefinitionFailure,
  fetchQCTaskDefinitionStatus,
  fetchQCTaskDefinitionStatusSuccess,
  fetchQCTaskDefinitionStatusFailure,
} = qcTaskDefinitionSlice.actions;

export const actions = { ...qcTaskDefinitionSlice.actions };

export const useQCTaskDefinitionSlice = createSliceHook(
  qcTaskDefinitionSlice.name,
  actions
);
